import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface HeadlineVariant {
  id: string;
  headline_text: string;
  subtitle: string | null;
  button_text: string | null;
}

export const useHeadlineVariant = () => {
  const [searchParams] = useSearchParams();
  
  // Synchroniczne odczytanie z localStorage przed inicjalizacją stanu
  const storedHeadline = typeof window !== 'undefined' ? localStorage.getItem('assigned_headline') : null;
  const storedSubtitle = typeof window !== 'undefined' ? localStorage.getItem('assigned_subtitle') : null;
  const storedButton = typeof window !== 'undefined' ? localStorage.getItem('assigned_button') : null;
  
  const [headline, setHeadline] = useState<string>(storedHeadline || "");
  const [subtitle, setSubtitle] = useState<string>(storedSubtitle || "");
  const [buttonText, setButtonText] = useState<string>(storedButton || "");
  const [variantId, setVariantId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeHeadline = async () => {
      try {
        // 0. Fetch default organic tag from settings
        const { data: settingsData } = await supabase
          .from('headline_settings')
          .select('setting_value')
          .eq('setting_key', 'default_organic_tag')
          .maybeSingle();
        
        const defaultOrganicTag = settingsData?.setting_value || null;

        // 1. Get or generate session ID
        let sid = localStorage.getItem('headline_session_id');
        if (!sid) {
          sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('headline_session_id', sid);
        }
        setSessionId(sid);

        let assignmentSource: 'random' | 'url_param' | 'returning' | 'tag_campaign' = 'random';
        let selectedVariant: HeadlineVariant | null = null;
        let campaignTag: string | null = null;

        // Save fbclid from Meta Ads for attribution
        const fbclid = searchParams.get('fbclid');
        if (fbclid) {
          localStorage.setItem('fbclid', fbclid);
        }

        // 2. Check UTM_CONTENT parameter FIRST (highest priority - Meta Ads standard)
        const utmContent = searchParams.get('utm_content');
        const utmCampaign = searchParams.get('utm_campaign');
        const tagParam = searchParams.get('tag');
        
        // Priority: utm_content > utm_campaign > tag
        const campaignParam = utmContent || utmCampaign || tagParam;
        if (campaignParam) {
          // Fetch all active variants and filter in JavaScript for case-insensitive match
          const { data: allVariants, error } = await supabase
            .from('headline_variants')
            .select('id, headline_text, subtitle, button_text, tags')
            .eq('is_active', true);

          if (allVariants && !error) {
            // Filter variants that have the tag (case-insensitive)
            const tagVariants = allVariants.filter(v => 
              v.tags && v.tags.some((t: string) => t.toLowerCase() === campaignParam.toLowerCase())
            );
            
            if (tagVariants.length > 0) {
              selectedVariant = tagVariants[Math.floor(Math.random() * tagVariants.length)];
              assignmentSource = 'tag_campaign';
              campaignTag = campaignParam; // Preserve original case from URL
              // Update localStorage with campaign variant and tag
              localStorage.setItem('assigned_variant_id', selectedVariant.id);
              localStorage.setItem('assigned_campaign_tag', campaignParam);
            }
          }
        }

        // 3. Check VARIANT parameter (for backward compatibility)
        if (!selectedVariant) {
          const variantParam = searchParams.get('v');
          if (variantParam && variantParam.length >= 8) {
            const { data: urlVariant, error } = await supabase
              .from('headline_variants')
              .select('id, headline_text, subtitle, button_text')
              .ilike('id', `${variantParam}%`)
              .eq('is_active', true)
              .limit(1)
              .maybeSingle();

            if (urlVariant && !error) {
              selectedVariant = urlVariant;
              assignmentSource = 'url_param';
              // Update localStorage with campaign variant
              localStorage.setItem('assigned_variant_id', urlVariant.id);
            }
          }
        }

        // 4. If no URL param or variant not found, check localStorage (returning user)
        if (!selectedVariant) {
          const assignedVariantId = localStorage.getItem('assigned_variant_id');
          
          if (assignedVariantId) {
            const { data: storedVariant, error } = await supabase
              .from('headline_variants')
              .select('id, headline_text, subtitle, button_text')
              .eq('id', assignedVariantId)
              .eq('is_active', true)
              .maybeSingle();

            if (storedVariant && !error) {
              selectedVariant = storedVariant;
              assignmentSource = 'returning';
            }
          }
        }

        // 4. If still no variant, assign from default organic tag (new organic user)
        if (!selectedVariant) {
          const { data: allVariants, error } = await supabase
            .from('headline_variants')
            .select('id, headline_text, subtitle, button_text, tags')
            .eq('is_active', true);

          if (error || !allVariants || allVariants.length === 0) {
            console.error('Error fetching variants:', error);
            // Fallback values - always display content
            setHeadline("Czy Twoja firma w pełni wykorzystuje potencjał LinkedIn?");
            setSubtitle("Sprawdź w 2 minuty możliwości Twojej firmy do generowania sprzedaży.");
            setButtonText("Rozpocznij quiz");
            setIsLoading(false);
            return;
          }

          // Filter by default tag if one is configured (case-insensitive)
          let variantsPool = allVariants;
          if (defaultOrganicTag) {
            const defaultTagVariants = allVariants.filter(v => 
              v.tags && v.tags.some((t: string) => t.toLowerCase() === defaultOrganicTag.toLowerCase())
            );
            
            // If variants with default tag exist, use them; otherwise fallback to all
            if (defaultTagVariants.length > 0) {
              variantsPool = defaultTagVariants;
              campaignTag = defaultOrganicTag;
            }
          }
          
          selectedVariant = variantsPool[Math.floor(Math.random() * variantsPool.length)];
          assignmentSource = 'random';
          localStorage.setItem('assigned_variant_id', selectedVariant.id);
        }

        // 5. Save assignment to database with source tracking
        if (selectedVariant) {
          const userAgent = navigator.userAgent;
          
          // Check if this session already has an assignment to avoid duplicates
          const { data: existingAssignment } = await supabase
            .from('headline_assignments')
            .select('id')
            .eq('session_id', sid)
            .maybeSingle();
          
          // Only insert if no assignment exists for this session
          if (!existingAssignment) {
            const storedFbclid = localStorage.getItem('fbclid');
            const { error: assignError } = await supabase
              .from('headline_assignments')
              .insert({
                variant_id: selectedVariant.id,
                session_id: sid,
                user_agent: userAgent,
                assignment_source: assignmentSource,
                campaign_tag: campaignTag,
                fbclid: storedFbclid
              });

            if (assignError) {
              console.error('Error saving assignment:', assignError);
            }
          }

          setHeadline(selectedVariant.headline_text);
          setSubtitle(selectedVariant.subtitle || "Sprawdź w 2 minuty możliwości Twojej firmy do generowania sprzedaży, budowania wizerunku lidera branżowego  i otrzymaj spersonalizowane zalecenia dotyczące LinkedIn.");
          setButtonText(selectedVariant.button_text || "Rozpocznij quiz");
          setVariantId(selectedVariant.id);
          
          // Store in localStorage for tracking (both old and new keys for backward compatibility)
          localStorage.setItem('assigned_headline', selectedVariant.headline_text);
          localStorage.setItem('assigned_subtitle', selectedVariant.subtitle || '');
          localStorage.setItem('assigned_button', selectedVariant.button_text || '');
          localStorage.setItem('variantId', selectedVariant.id); // For B2C exit tracking
          localStorage.setItem('sessionId', sid); // For B2C exit tracking
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing headline:', error);
        // Fallback values - always display content
        setHeadline("Czy Twoja firma w pełni wykorzystuje potencjał LinkedIn?");
        setSubtitle("Sprawdź w 2 minuty możliwości Twojej firmy do generowania sprzedaży.");
        setButtonText("Rozpocznij quiz");
        setIsLoading(false);
      }
    };

    initializeHeadline();
  }, [searchParams]);

  return { headline, subtitle, buttonText, variantId, sessionId, isLoading };
};
